import { useOutletContext } from "react-router";
import type { SchrijvenVariantContext } from "@a2/core";
import type { SessionUser } from "../../lib/session-loader";
import { useT } from "../../lib/use-t";

interface EmailComposerShellProps {
  variant: SchrijvenVariantContext;
  value: string;
  onChange: (text: string) => void;
  disabled?: boolean;
  /** Decorative, client-only e-mail subject. Not sent to the grader (see spec §3a). */
  subject?: string;
  onSubjectChange?: (text: string) => void;
}

/**
 * Email composer — a webmail compose frame: an "Aan" header row with a
 * draft save-state pill, a decorative "Onderwerp" subject input (kept
 * client-side, not graded), then the locked greeting + body textarea +
 * locked closing. The student writes only the middle.
 */
export function EmailComposerShell({
  variant,
  value,
  onChange,
  disabled = false,
  subject = "",
  onSubjectChange,
}: EmailComposerShellProps) {
  const session = useOutletContext<SessionUser>();
  const t = useT();
  const displayName = session?.displayName?.trim() || "Ik";

  return (
    <article className="flex flex-col min-h-0 flex-1 rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] shadow-[var(--shadow)] overflow-hidden">
      <header className="flex items-center gap-3 px-5 py-3.5 border-b border-[var(--border)]">
        <span className="text-[13px] text-[var(--ink-muted)]">
          {t("schrijven.runner.toLabel")}: <b className="text-[var(--ink)]">{variant.recipient ?? "—"}</b>
        </span>
        <span className="ml-auto inline-flex items-center gap-1.5 font-mono-badge text-xs text-[var(--ink-faint)]">
          <span className="inline-block w-[7px] h-[7px] rounded-full bg-[var(--pass)]" aria-hidden />
          {t("schrijven.runner.saved")}
        </span>
      </header>

      <div className="px-6 pt-4 pb-1.5">
        <label className="block font-mono-badge text-[10px] uppercase tracking-wide text-[var(--ink-faint)] mb-1">
          {t("schrijven.runner.subjectLabel")}
        </label>
        <input
          value={subject}
          onChange={(e) => onSubjectChange?.(e.target.value)}
          disabled={disabled || !onSubjectChange}
          placeholder={t("schrijven.runner.subjectPlaceholder")}
          className="w-full bg-transparent border-0 outline-none font-display font-semibold text-[21px] tracking-tight text-[var(--ink)] placeholder:text-[var(--ink-faint)] placeholder:font-normal disabled:opacity-60"
          aria-label={t("schrijven.runner.subjectLabel")}
        />
      </div>

      <div className="flex-1 min-h-0 px-6 pt-2 pb-5 overflow-auto space-y-3">
        {variant.greeting ? (
          <p className="text-[16px] text-[var(--ink)] leading-relaxed">{variant.greeting}</p>
        ) : null}

        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={9}
          placeholder={t("schrijven.runner.emailPlaceholder")}
          className="w-full rounded-md bg-[var(--bg)] border border-[var(--border)] px-3 py-2.5 text-[16px] text-[var(--ink)] leading-relaxed placeholder:text-[var(--ink-faint)] resize-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2 disabled:opacity-50"
          aria-label={t("schrijven.runner.emailBodyLabel")}
        />

        {variant.closing ? (
          <div className="text-[16px] text-[var(--ink)] leading-relaxed">
            <p>{variant.closing}</p>
            <p>{displayName}</p>
          </div>
        ) : null}
      </div>
    </article>
  );
}
